import {
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  PaginationRoot,
} from './pagination-primitive';

interface PaginationProps {
  currentPageIndex: number;
  totalPages: number;
  onPageChange?: (pageIndex: number) => void;
  windowSize?: number;
  disabled?: boolean;
  className?: string;
}

export function Pagination({
  currentPageIndex,
  totalPages,
  onPageChange,
  windowSize = 1,
  disabled = false,
  className,
}: PaginationProps) {
  const currentPage = currentPageIndex + 1;

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];

    // Always show first page
    pages.push(1);

    // Calculate window around current page
    const windowStart = Math.max(2, currentPage - windowSize);
    const windowEnd = Math.min(totalPages - 1, currentPage + windowSize);

    // Add ellipsis after first page if needed
    if (windowStart > 2) {
      pages.push('ellipsis');
    }

    // Add pages in the window
    for (let i = windowStart; i <= windowEnd; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (windowEnd < totalPages - 1) {
      pages.push('ellipsis');
    }

    // Always show last page (if there's more than one page)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageClick = (pageIndex: number) => {
    onPageChange?.(pageIndex);
  };

  const pages = getPageNumbers();

  return (
    <PaginationRoot className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => {
              handlePageClick(Math.max(0, currentPageIndex - 1));
            }}
            disabled={disabled || currentPage === 1}
          />
        </PaginationItem>

        {pages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }

          return (
            <PaginationItem key={page}>
              <PaginationButton
                onClick={() => {
                  handlePageClick(page - 1);
                }}
                isActive={currentPage === page}
                disabled={disabled}
              >
                {page}
              </PaginationButton>
            </PaginationItem>
          );
        })}

        <PaginationItem>
          <PaginationNext
            onClick={() => {
              handlePageClick(Math.min(totalPages - 1, currentPageIndex + 1));
            }}
            disabled={disabled || currentPage === totalPages}
          />
        </PaginationItem>
      </PaginationContent>
    </PaginationRoot>
  );
}
